import React from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import { labelForSlug, supportsTargetModeSlug } from '../../../utils/constants/overlayTypes'
import useRackStyles from './rackStyles'
import { EyeOff } from './railIcons'

// Best Performance is scoped to a quarter, and the whole game's best performer is its own
// overlay — so the game option on this row shows MVP. MVP therefore gets no row of its own.
const QUARTER_SCOPED_SLUG = 'best-performance'
const GAME_SLUG = 'mvp'
const SCOPES = [
  { label: 'Q1', quarter: 1 },
  { label: 'Q2', quarter: 2 },
  { label: 'Q3', quarter: 3 },
  { label: 'Q4', quarter: 4 },
  { label: 'Game', slug: GAME_SLUG },
]

// The rack's running order. A slug the backend adds and this list does not know falls in
// after these, keeping the order it arrived in.
const ORDER = [
  'roster',
  'starters',
  'scorebanner',
  'team-comparison',
  'team-shots',
  QUARTER_SCOPED_SLUG,
]
const rank = (type) => (ORDER.indexOf(type) === -1 ? ORDER.length : ORDER.indexOf(type))

// Names the rack uses in place of the shared slug table's, which also labels the branding
// and default-overlay surfaces.
const TEAM_SIDES = [
  { side: 'home', name: 'Home Team' },
  { side: 'away', name: 'Away Team' },
]

const RACK_LABELS = {
  [QUARTER_SCOPED_SLUG]: 'Best Performance of',
  'team-shots': 'Shotchart',
}

/**
 * The rack of cards an operator can put on the broadcast. One card shows at a time, so
 * choosing another replaces it and choosing the live one again clears the screen.
 *
 * `cards` is the backend's answer and is never second-guessed here: an empty list means this
 * game has nothing to show and the rail keeps the tool disabled. `pending` is the card the
 * streamer is still rendering and `failed` the one it gave up on.
 */
const OverlayPanel = ({ cards, active, pending, failed, onShow, onHide, busy }) => {
  const classes = useRackStyles()

  const isLive = (type, { team = null, quarter = null } = {}) =>
    active?.type === type &&
    (team === null || active?.team === team) &&
    (quarter === null || active?.quarter === quarter)

  const choose = (type, { team = null, quarter = null } = {}) => {
    if (busy || pending) return
    if (isLive(type, { team, quarter })) onHide()
    else onShow({ overlayType: type, team, quarter })
  }

  // The game option puts a different overlay up, so the row is live under either slug.
  const rowIsLive = (card) =>
    isLive(card.type) || (card.type === QUARTER_SCOPED_SLUG && isLive(GAME_SLUG))

  // MVP is reachable as this row's game option, so it gets no row of its own.
  const rack = cards
    .filter((card) => card.type !== GAME_SLUG)
    .sort((a, b) => rank(a.type) - rank(b.type))

  const rowIsPending = (card) =>
    pending?.type === card.type ||
    (card.type === QUARTER_SCOPED_SLUG && pending?.type === GAME_SLUG)

  const stateLabel = (card) => {
    if (rowIsLive(card)) return 'Live'
    // Only the two types that can be branded either way are worth explaining.
    return !card.requiresTeam && supportsTargetModeSlug(card.type) ? 'Both teams' : ''
  }

  return (
    <div className={classes.list}>
      {rack.map((card) => {
        const quarterScoped = card.type === QUARTER_SCOPED_SLUG
        const label = RACK_LABELS[card.type] ?? labelForSlug(card.type)
        // Both kinds of scoped card are put on air by their own buttons, so the row
        // above them is a label rather than a control.
        const scoped = card.requiresTeam || quarterScoped
        return (
          <div
            key={card.type}
            className={`${classes.card} ${rowIsLive(card) ? classes.cardLive : ''}`}
          >
            <button
              type='button'
              className={`${classes.cardButton} ${scoped ? classes.cardButtonInert : ''}`}
              title={
                card.requiresTeam
                  ? `Pick a team to show ${label}`
                  : quarterScoped
                    ? `Pick a quarter to show ${label}`
                    : `Toggle ${label}`
              }
              onClick={() => !scoped && choose(card.type)}
              aria-pressed={rowIsLive(card)}
            >
              <span className={classes.cardLabel}>{label}</span>
              <span className={classes.cardState}>
                {rowIsPending(card) ? (
                  <CircularProgress size={12} color='inherit' aria-label='Rendering' />
                ) : (
                  stateLabel(card)
                )}
              </span>
            </button>

            {card.requiresTeam && (
              <div className={classes.teams}>
                {TEAM_SIDES.map(({ side, name }) => (
                  <button
                    key={side}
                    type='button'
                    className={`${classes.team} ${
                      isLive(card.type, { team: side }) ? classes.teamSelected : ''
                    }`}
                    onClick={() => choose(card.type, { team: side })}
                    aria-pressed={isLive(card.type, { team: side })}
                    title={`${label}: ${name}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}

            {quarterScoped && (
              <div className={classes.teams}>
                {SCOPES.map(({ label: scopeLabel, quarter, slug }) => {
                  const live = slug ? isLive(slug) : isLive(card.type, { quarter })
                  return (
                    <button
                      key={scopeLabel}
                      type='button'
                      className={`${classes.team} ${live ? classes.teamSelected : ''}`}
                      onClick={() => (slug ? choose(slug) : choose(card.type, { quarter }))}
                      aria-pressed={live}
                      title={`${label} ${scopeLabel}`}
                    >
                      {scopeLabel}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {failed && (
        <div className={classes.note} role='alert'>
          {`${RACK_LABELS[failed.type] ?? labelForSlug(failed.type)} could not be rendered and was hidden.`}
        </div>
      )}

      {/* Deliberately not conditional on knowing what is live. An operator who has just
            walked in on a colleague's card — or on a team card whose side the streamer does
            not report — still needs one certain way to clear the screen. The DELETE behind
            it is idempotent, so pressing it with nothing showing costs nothing. */}
      <button
        type='button'
        className={classes.hideRow}
        onClick={() => !busy && onHide()}
        disabled={busy}
        title='Clear whatever is on screen'
      >
        <EyeOff className={classes.hideIcon} />
        <span className={classes.cardLabel}>Hide</span>
      </button>
    </div>
  )
}

OverlayPanel.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      requiresTeam: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  active: PropTypes.shape({
    type: PropTypes.string,
    team: PropTypes.string,
    quarter: PropTypes.number,
  }),
  pending: PropTypes.shape({ type: PropTypes.string }),
  failed: PropTypes.shape({ type: PropTypes.string }),
  onShow: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  busy: PropTypes.bool,
}

OverlayPanel.defaultProps = { active: null, pending: null, failed: null, busy: false }

export default OverlayPanel
